import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import {
  getBrandCampaigns,
  getBrandCampaignById,
  getCampaignStatsByBrand,
  updateBrandCampaign,
  reRunCampaign,
  deleteDraftCampaign,
} from '../../services/userService';
import { Campaign } from '../../interfaces/campaign';

const STALE_TIME = 0;

export const useGetBrandCampaignsInfiniteQuery = (
  status: string,
  enabled = true,
) =>
  useInfiniteQuery({
    queryKey: queryKeys.campaigns.byStatusInfinite(status),
    queryFn: ({ pageParam }) =>
      getBrandCampaigns(status, pageParam as number, 2),
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, allPages) =>
      lastPage?.pagination?.hasNextPage ? allPages.length + 1 : undefined,
    staleTime: STALE_TIME,
    enabled,
  });

export const useGetCampaignByIdQuery = (campaignId: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.campaigns.byId(campaignId),
    queryFn: () => getBrandCampaignById(campaignId),
    staleTime: STALE_TIME,
    enabled: enabled && !!campaignId,
  });

export const useGetBrandCampaignsPaginatedQuery = (
  status: string,
  page: number,
  limit: number,
) =>
  useQuery({
    queryKey: queryKeys.campaigns.byStatus(status, page, limit),
    queryFn: () => getBrandCampaigns(status, page, limit),
    staleTime: STALE_TIME,
  });

export const useGetCampaignStatsQuery = (brandId: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.campaigns.stats(brandId),
    queryFn: () => getCampaignStatsByBrand(brandId),
    staleTime: STALE_TIME,
    enabled: enabled && !!brandId,
  });

export const useUpdateCampaignMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      updateData,
    }: {
      id: string;
      updateData: Partial<Campaign>;
    }) => updateBrandCampaign(id, updateData),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.byId(id),
      });
      queryClient.invalidateQueries({
        queryKey: ['campaigns', 'byStatus'],
      });
      queryClient.invalidateQueries({
        queryKey: ['campaigns', 'infinite'],
      });
    },
  });
};

export const useReRunCampaignMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reRunCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['campaigns', 'byStatus'],
      });
      queryClient.invalidateQueries({
        queryKey: ['campaigns', 'infinite'],
      });
    },
  });
};

export const useDeleteDraftCampaignMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftId: string) => deleteDraftCampaign(draftId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['campaigns', 'byStatus', 'draft'],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.byStatusInfinite('draft'),
      });
    },
  });
};
